import { IconButton, Stack, Tooltip } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import MailIcon from '@mui/icons-material/Mail'
import DeleteIcon from '@mui/icons-material/Delete'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { DeleteManagementForm, SendManagementReminders } from '../../../../services/peerToPeer/ManagementService'
import { handleApiResponse } from '../../../../utils/apiHelpers'
import { CorrectionDialog } from './CorrectionDialog'

const actionIcon = { fontSize: '20px', color: 'var(--grey-700)' }

interface Props {
  formId: number | null
  districtOrganizationId: number | null
  reportingYearId: number
  onChanged: () => void
}

export const BuildingActions = ({ formId, districtOrganizationId, reportingYearId, onChanged }: Props): JSX.Element => {
  const [editing, setEditing] = useState(false)

  const remind = async (): Promise<void> => {
    // A Not-started row has no form id; remind by its district instead so the backend
    // can resolve the invited submitter(s) — otherwise there is nothing to target.
    const byForm = formId != null ? { p2pBuildingFormIds: [formId], reportingYearId } : null
    const byDistrict = districtOrganizationId != null ? { districtOrganizationIds: [districtOrganizationId], reportingYearId } : null
    const request = byForm ?? byDistrict
    if (request == null) { toast.error('No submitter to remind for this building.'); return }

    const result = await handleApiResponse(SendManagementReminders(request))
    if (result == null) return
    if (result.remindersSent > 0) toast.success(`Reminder sent to ${result.remindersSent} submitter${result.remindersSent === 1 ? '' : 's'}.`)
    else toast.info('No invited submitter to remind for this building yet.')
  }

  const remindButton = (testId: string): JSX.Element => (
    <Tooltip title='Send reminder'>
      <IconButton size='small' data-testid={testId} onClick={() => { void remind() }}><MailIcon sx={actionIcon} /></IconButton>
    </Tooltip>
  )

  if (formId == null) return remindButton('p2pRemindRow')

  const del = async (): Promise<void> => {
    const result = await handleApiResponse(DeleteManagementForm(formId))
    if (result !== null) { toast.success('Submission deleted'); onChanged() }
  }

  return (
    <Stack direction='row' gap='2px'>
      <Tooltip title='Edit'>
        <IconButton size='small' data-testid={`p2pEditForm-${formId}`} onClick={() => { setEditing(true) }}><EditIcon sx={actionIcon} /></IconButton>
      </Tooltip>
      {remindButton(`p2pRemindForm-${formId}`)}
      <Tooltip title='Delete'>
        <IconButton size='small' data-testid={`p2pDeleteForm-${formId}`} onClick={() => { void del() }}><DeleteIcon sx={actionIcon} /></IconButton>
      </Tooltip>

      <CorrectionDialog
        formId={formId}
        open={editing}
        onClose={() => { setEditing(false) }}
        onSaved={() => { setEditing(false); onChanged() }}
      />
    </Stack>
  )
}
